import { z } from 'zod';
import { planKeySchema } from './plans.js';
import { roleSchema, timestampSchema, ulidSchema, providerIdSchema } from './common.js';

export const ACCESS_SOURCES = ['membership', 'staff'] as const;
export const accessSourceSchema = z.enum(ACCESS_SOURCES);
export type AccessSource = z.infer<typeof accessSourceSchema>;

/**
 * The result of `domain/access.ts:getAccess`, the only place Access is computed.
 * Mirrors `01-product-rules.md` section 2.
 */
export const accessSchema = z.object({
  hasAccess: z.boolean(),
  source: accessSourceSchema.nullable(),
  /** Unix ms when Access ends. Null when the user has no Access. */
  accessUntil: timestampSchema.nullable(),
  /** The user's own plan, if they pay for one. Null for guests and staff who do not pay. */
  planKey: planKeySchema.nullable(),
});
export type Access = z.infer<typeof accessSchema>;

export const membershipSummarySchema = z.object({
  id: providerIdSchema,
  planKey: planKeySchema,
  valid: z.boolean(),
  status: z.string(),
  currentPeriodEnd: timestampSchema.nullable(),
  cancelAtPeriodEnd: z.boolean(),
});
export type MembershipSummary = z.infer<typeof membershipSummarySchema>;

/** `GET /v1/me`. The client refetches this on focus and every 5 minutes. */
export const meSchema = z.object({
  user: z.object({
    id: ulidSchema,
    discordUserId: z.string(),
    username: z.string(),
    displayName: z.string().nullable(),
    avatarUrl: z.string().nullable(),
    role: roleSchema,
    createdAt: timestampSchema,
  }),
  access: accessSchema,
  membership: membershipSummarySchema.nullable(),
  /** Games this user is linked to as a dev. Empty for everyone else. */
  devGameIds: z.array(ulidSchema),
});
export type Me = z.infer<typeof meSchema>;

/**
 * Body of the signed Access pass JWT (`02-api-and-data.md` section 7.1).
 *
 * Two things can put a game in reach, so the pass carries both:
 *
 * - `src` is a membership or staff, and covers everything we publish. Null for
 *   somebody who has only ever bought games.
 * - `own` is the games they own outright. Present whether or not `src` is, because a member who
 *   also bought a game keeps that game when the membership ends and the pass has to say so.
 *
 * A pass with neither is never issued: `POST /passes/access` refuses instead, which is the 403
 * that tells an app to delete its stored pass.
 */
export const accessPassClaimsSchema = z.object({
  typ: z.literal('access'),
  sub: ulidSchema,
  src: accessSourceSchema.nullable(),
  /** Game ids owned outright. Omitted rather than empty when there are none, to keep it small. */
  own: z.array(ulidSchema).optional(),
  iat: z.number().int(),
  exp: z.number().int(),
});
export type AccessPassClaims = z.infer<typeof accessPassClaimsSchema>;

export const accessPassResponseSchema = z.object({
  pass: z.string(),
  hasAccess: z.boolean(),
  accessUntil: timestampSchema,
  source: accessSourceSchema.nullable(),
});
export type AccessPassResponse = z.infer<typeof accessPassResponseSchema>;
